import {
  Cart,
  CartConflict,
  CartItem,
  CartValidationResult,
  ClinicProduct,
} from '../types';
import { delay } from './delay';
import { db, findProductById, getOrCreateCart } from './db';
import { ERR_NOT_FOUND, ERR_VALIDATION } from './errors';
import { readUserIdFromToken } from './auth-utils';

const round2 = (n: number): number => Math.round(n * 100) / 100;

const buildCart = (lines: { productId: string; quantity: number }[]): Cart => {
  const items: CartItem[] = [];
  let subtotal = 0;
  let gstTotal = 0;
  for (const line of lines) {
    const product = findProductById(line.productId);
    if (!product) continue;
    const lineTotal = round2(product.salePrice * line.quantity);
    const gst = round2(lineTotal * product.gstRate);
    subtotal += lineTotal;
    gstTotal += gst;
    items.push({
      productId: product.id,
      quantity: line.quantity,
      product: { ...product },
      lineTotal,
    });
  }
  subtotal = round2(subtotal);
  gstTotal = round2(gstTotal);
  return {
    items,
    subtotal,
    gstTotal,
    total: round2(subtotal + gstTotal),
  };
};

const validateProductLine = (
  product: ClinicProduct,
  quantity: number,
): CartConflict | null => {
  if (product.stockStatus === 'OUT_OF_STOCK' || product.availableStock <= 0) {
    return {
      productId: product.id,
      reason: 'OUT_OF_STOCK',
      message: `${product.nameEn} is out of stock`,
    };
  }
  if (quantity > product.availableStock) {
    return {
      productId: product.id,
      reason: 'INSUFFICIENT_STOCK',
      message: `Only ${product.availableStock} ${product.unit} available for ${product.nameEn}`,
      availableStock: product.availableStock,
    };
  }
  if (product.moq && product.moq > 1 && quantity < product.moq) {
    return {
      productId: product.id,
      reason: 'BELOW_MOQ',
      message: `Minimum order quantity for ${product.nameEn} is ${product.moq}`,
      moq: product.moq,
    };
  }
  return null;
};

export const cartMock = {
  get: async (): Promise<Cart> => {
    await delay(60, 180);
    const userId = readUserIdFromToken();
    const record = getOrCreateCart(userId);
    return buildCart(record.items);
  },

  addItem: async (productId: string, quantity: number): Promise<Cart> => {
    await delay();
    const userId = readUserIdFromToken();
    const product = findProductById(productId);
    if (!product) throw ERR_NOT_FOUND('Product');
    if (quantity <= 0) throw ERR_VALIDATION('Quantity must be greater than 0');

    const record = getOrCreateCart(userId);
    const existing = record.items.find((i) => i.productId === productId);
    const nextQty = (existing?.quantity ?? 0) + quantity;

    const conflict = validateProductLine(product, nextQty);
    if (conflict) throw ERR_VALIDATION(conflict.message, { conflict });

    if (existing) existing.quantity = nextQty;
    else record.items.push({ productId, quantity });
    return buildCart(record.items);
  },

  updateItem: async (productId: string, quantity: number): Promise<Cart> => {
    await delay();
    const userId = readUserIdFromToken();
    const record = getOrCreateCart(userId);
    const existing = record.items.find((i) => i.productId === productId);
    if (!existing) throw ERR_NOT_FOUND('Cart item');
    if (quantity <= 0) {
      record.items = record.items.filter((i) => i.productId !== productId);
      return buildCart(record.items);
    }
    const product = findProductById(productId);
    if (!product) throw ERR_NOT_FOUND('Product');
    const conflict = validateProductLine(product, quantity);
    if (conflict) throw ERR_VALIDATION(conflict.message, { conflict });
    existing.quantity = quantity;
    return buildCart(record.items);
  },

  removeItem: async (productId: string): Promise<Cart> => {
    await delay();
    const userId = readUserIdFromToken();
    const record = getOrCreateCart(userId);
    record.items = record.items.filter((i) => i.productId !== productId);
    return buildCart(record.items);
  },

  validate: async (): Promise<CartValidationResult> => {
    await delay();
    const userId = readUserIdFromToken();
    const record = getOrCreateCart(userId);
    const conflicts: CartConflict[] = [];
    for (const line of record.items) {
      const product = findProductById(line.productId);
      if (!product) {
        conflicts.push({
          productId: line.productId,
          reason: 'DELISTED',
          message: 'Product is no longer available',
        });
        continue;
      }
      const conflict = validateProductLine(product, line.quantity);
      if (conflict) conflicts.push(conflict);
    }
    return {
      valid: conflicts.length === 0,
      conflicts,
      cart: buildCart(record.items),
    };
  },

  clear: async (): Promise<void> => {
    const userId = readUserIdFromToken();
    const record = getOrCreateCart(userId);
    record.items = [];
  },
};
